import { useRef, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import statesTopology from "us-atlas/states-10m.json";
import { SERVICE_STATES, STATE_OPTIONS, scrollToId } from "../data/siteData";

export default function StateMap() {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);

  const onMove = (e, fips) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const s = SERVICE_STATES[fips];
    setHover({
      ...s,
      x: Math.min(Math.max(e.clientX - rect.left, 80), rect.width - 90),
      y: Math.max(e.clientY - rect.top - 14, 10),
    });
  };

  return (
    <section id="service-states" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-s font-bold uppercase tracking-[0.2em] text-[#0066FF]">Where We Serve</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B1F44]">
            Active in 16 deregulated markets
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            If your business operates in a highlighted state, you may have the power to choose
            your commercial energy supplier. Hover over the map to explore. Eligibility varies by utility territory.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          <div ref={wrapRef} className="relative lg:col-span-2" data-testid="state-map">
            <ComposableMap projection="geoAlbersUsa" width={980} height={560} style={{ width: "100%", height: "auto" }}>
              <Geographies geography={statesTopology}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const fips = String(geo.id).padStart(2, "0");
                    const covered = SERVICE_STATES[fips];
                    const isHover = covered && hover?.abbr === covered.abbr;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        data-testid={covered ? `state-map-node-${covered.abbr.toLowerCase()}` : undefined}
                        onMouseMove={covered ? (e) => onMove(e, fips) : undefined}
                        onMouseLeave={() => setHover(null)}
                        onClick={covered ? () => scrollToId("get-quote") : undefined}
                        fill={covered ? (isHover ? "#0052CC" : "#0066FF") : "#DCE6F2"}
                        stroke="#FFFFFF"
                        strokeWidth={0.8}
                        style={{
                          outline: "none",
                          cursor: covered ? "pointer" : "default",
                          transition: "fill 0.2s ease",
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            {hover && (
              <div
                className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-xl bg-[#0B1F44] px-4 py-2.5 text-white shadow-xl"
                style={{ left: hover.x, top: hover.y }}
                data-testid="state-map-tooltip"
              >
                <p className="text-sm font-bold">{hover.name}</p>
                <p className="text-xs text-blue-200">{hover.commodity}</p>
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-6 sm:p-8 border border-slate-100 shadow-[0_10px_30px_-5px_rgba(11,31,68,0.08)]">
            <h3 className="text-lg font-semibold text-[#0B1F44]">
              {hover ? hover.name : "Markets vary by location"}
            </h3>
            <p className="mt-1.5 text-sm text-slate-500">
              {hover
                ? `Deregulated ${hover.commodity.toLowerCase()} supply, click the state to request a quote.`
                : "Hover or tap a highlighted state to see its deregulated markets."}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {STATE_OPTIONS.map((s) => (
                <span
                  key={s.abbr}
                  onMouseEnter={() => {
                    const fips = Object.keys(SERVICE_STATES).find((k) => SERVICE_STATES[k].abbr === s.abbr);
                    setHover({ ...SERVICE_STATES[fips], x: -999, y: -999 });
                  }}
                  onMouseLeave={() => setHover(null)}
                  className="rounded-full border border-[#0066FF]/25 bg-[#E6EEF7] px-3 py-1 text-xs font-semibold text-[#0B1F44] hover:bg-[#0066FF] hover:text-white transition-colors cursor-default"
                >
                  {s.abbr}
                </span>
              ))}
            </div>
            <button
              onClick={() => scrollToId("get-quote")}
              data-testid="state-map-cta"
              className="mt-6 w-full rounded-full bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold py-3 transition-colors"
            >
              Check Rates in My State
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
